import { useState } from "react";
import { clsx } from "clsx";

/**
 * Tag editor — chip per tag with removable × and a trailing add-input.
 *
 * Enter adds; Backspace on empty input removes the last chip; duplicate adds
 * trigger a short shake (CSS `@keyframes tag-shake`).
 */
export function TagChipsEditor({
  tags,
  onChange,
  disabled,
}: {
  tags: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const [shake, setShake] = useState(false);

  const triggerShake = () => {
    setShake(true);
    window.setTimeout(() => setShake(false), 220);
  };

  const addFromDraft = () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (tags.includes(v)) {
      triggerShake();
      setDraft("");
      return;
    }
    onChange([...tags, v]);
    setDraft("");
  };

  const removeAt = (idx: number) => {
    if (disabled) return;
    const next = tags.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addFromDraft();
    } else if (e.key === "Backspace" && draft === "" && tags.length > 0) {
      e.preventDefault();
      removeAt(tags.length - 1);
    }
  };

  return (
    <div className={clsx("tag-editor", shake && "is-shake")}>
      {tags.map((t, i) => (
        <span key={`${t}-${i}`} className="tag-chip">
          {t}
          {disabled ? null : (
            <button
              type="button"
              aria-label={`Remove tag ${t}`}
              onClick={() => removeAt(i)}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {!disabled ? (
        <input
          className="tag-input"
          value={draft}
          placeholder="+ add tag"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={addFromDraft}
          onKeyDown={onKey}
          aria-label="Add tag"
        />
      ) : null}
    </div>
  );
}
